import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import * as Cesium from 'cesium';
import { useCesium } from '../../context/CesiumContext';
import { useCesiumClock } from '../../hooks/useCesiumClock';
import { useMDCockpit } from '../../hooks/useMDCockpit';
import { getReportUsedEntities } from '../../lib/accessAnalysisReports';
import { showToastMessage } from '../../../../../utils/commonFunctions';
import { epochToDisplay } from '../../lib/datetime';
import type { AccessWindowCounterpartyKind, AccessWindowPair, GeoFeature, GroundStation, SatGroup, Satellite, TreeSatItem } from '../../types/mission';
import CustomToolTip from '../../../../CommonComponents/CustomToolTip';
import { SkipBackIcon, SkipFwdIcon, StepBackIcon, StepFwdIcon, RefreshIcon } from '../Common/components/svgs';
import styles from './Timeline.module.scss';
import { PauseIcon, PlayIcon } from '../../../../GenericSimulator/components/dashboard/timeline/Svgs';

import {
  Timeline as CommonTimeline,
  TimelineBodyContainer,
  TimelineContainer,
  TimelineHeader,
  TimelineScrollContainer,
  TimelineTrackContainer,
  TimelineEvent,
  TimelineTrack,
  TimelineContainerDevider,
  TimelineEventTrack,
  RealtimeTracker,
  SpeedoMeter,
  LiveButton,
} from '../../../../CommonComponents/timeline/Timeline';
import { DropdownArrow, RightArrow, TimeIntervalSvg } from '../../../../CommonComponents/svgs/Svgs';
import OmniSelect from '../../../../CommonComponents/omni-select/OmniSelect';
import { TimeFrameInMinutes, TimeFrameOptions } from '../../../../CommonComponents/timeline/constant';

const SPEEDS = [1, 2, 4, 10, 60, 120, 240, 540];
const TIMELINE_MIN_HEIGHT = 96;
const TIMELINE_MAX_HEIGHT = 400;
const MINIMUM_TIMELINE_DURATION = 1440

interface TimelineProps {
  updateLocked?: boolean;
  updateReady?: boolean;
  updateDisabledReason?: string;
  onMissionUpdate?: () => Promise<void>;
  accessWindowStatus?: 'ready' | 'computing' | 'failed' | null;
  accessWindowRunState?: { pairsTotal?: number; pairsDone?: number; pairsFailed?: number } | null;
}

type UpdateState = 'idle' | 'updating' | 'success' | 'failed';

interface TimelinePassBlock {
  id: string;
  pairKey: string;
  satelliteId: string;
  satelliteName: string;
  status: string;
  errorReason?: string | null;
  start: number;
  end: number;
  durationSec: number;
  maxElevationDeg: number;
  counterpartyId: string;
  counterpartyKind: AccessWindowCounterpartyKind;
  counterpartyName: string;
}

interface TimelineRow {
  id: string;
  kind: AccessWindowCounterpartyKind;
  label: string;
  icon: string;
  blocks: TimelinePassBlock[];
}

type TimelineDataType = {
  name: string
  values: TimelineRow[]
  id: AccessWindowCounterpartyKind
  icon: string
}

const toEpoch = (value: number | string | undefined | null): number | null => {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? Math.floor(parsed / 1000) : null;
};

const flattenSatellites = (items: TreeSatItem[]): Satellite[] => {
  const list: Satellite[] = [];
  items.forEach(item => {
    if ((item as SatGroup).type === 'group') {
      list.push(...(item as SatGroup).children);
    } else {
      list.push(item as Satellite);
    }
  });
  return list;
};

const getKindIcon = (kind: AccessWindowCounterpartyKind) => {
  if (kind === 'satellite') return 'SAT';
  if (kind === 'ground_station') return 'GS';
  if (kind === 'geo_feature_geofence') return 'GF';
  return 'TGT';
};

const getKindLabel = (kind: AccessWindowCounterpartyKind) => {
  if (kind === 'satellite') return 'SAT - SAT';
  if (kind === 'ground_station') return 'SAT - GS';
  if (kind === 'geo_feature_geofence') return 'SAT - GEOFENSE';
  return 'SAT - TARGET';
};

const EditIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: '4px', opacity: 0.6 }}>
    <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const ClockIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);



const Timeline: React.FC<TimelineProps> = ({
  updateLocked = false,
  updateReady = true,
  updateDisabledReason = 'Waiting for satellite calculations',
  onMissionUpdate,
  accessWindowStatus = null,
  accessWindowRunState = null,
}) => {
  const cesium = useCesium();
  const {
    modelData,
    satellites,
    groundStations,
    geoFeatures,
    accessWindowPairs,
    timelineCollapsed,
    timelineHeight,
    setTimelineCollapsed,
    setTimelineHeight,
    selectedReportDetail,
    selectedCoverageReportDetail,
    openModelConfig,
    timelineResetTrigger,
  } = useMDCockpit();
  const time = useCesiumClock(cesium.viewerRef);
  const resizeRef = useRef<{ pointerId: number; startY: number; startHeight: number } | null>(null);
  const hadTimelineContentRef = useRef(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (timelineResetTrigger > 0) {
      cesium.resetClockToModelStart();
      setPlaying(false);
      cesium.setClockPlaying(false);
    }
  }, [timelineResetTrigger]);
  const [speed, setSpeed] = useState(1);
  const [updateState, setUpdateState] = useState<UpdateState>('idle');
  const [timeEditing, setTimeEditing] = useState(false);
  const [timeDraft, setTimeDraft] = useState('');
  const timeInputRef = useRef<HTMLInputElement | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Record<AccessWindowCounterpartyKind, boolean>>({
    satellite: false,
    ground_station: false,
    geo_feature_spot: false,
    geo_feature_geofence: false,
  });

  const [hoveredEvent, setHoveredEvent] = useState<any | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);

  const formatTimelineDate = (epochSec: number) => {
    const d = new Date(epochSec * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    const year = d.getUTCFullYear();
    const month = pad(d.getUTCMonth() + 1);
    const day = pad(d.getUTCDate());
    const hours = pad(d.getUTCHours());
    const minutes = pad(d.getUTCMinutes());
    const seconds = pad(d.getUTCSeconds());
    return {
      date: `${year}-${month}-${day}`,
      time: `T${hours}:${minutes}:${seconds}Z`
    };
  };

  const formatTimelineDuration = (durationSec: number) => {
    const mins = Math.round(durationSec / 60);
    if (mins <= 0) {
      return `${Math.round(durationSec)} secs`;
    }
    return `${mins} mins`;
  };

  const satelliteList = useMemo(() => flattenSatellites(satellites), [satellites]);
  const activeReport = selectedReportDetail || selectedCoverageReportDetail;
  const usedEntities = useMemo(() => getReportUsedEntities(activeReport, satellites), [activeReport, satellites]);
  const isReportActive = activeReport !== null;
  const hasSatellites = satelliteList.length > 0;
  const playbackDisabled = !hasSatellites;
  let currentEpoch = (() => {
    const v = cesium.viewerRef.current;
    if (!v) return null;
    const date = Cesium.JulianDate.toDate(v.clock.currentTime);
    const epoch = Math.floor(date.getTime() / 1000);
    return Number.isFinite(epoch) ? epoch : null;
  })();

  const { timelineData, rows, windowStart, windowEnd } = useMemo(() => {
    const satelliteNames = new Map(satelliteList.map(sat => [sat.id, sat.name]));
    const gsNames = new Map(groundStations.map((gs: GroundStation) => [gs.id, gs.name]));
    const geoNames = new Map(geoFeatures.map((gf: GeoFeature) => [gf.id, gf.name]));

    const validPairs = (accessWindowPairs as AccessWindowPair[]).filter(pair => {
      if (!pair?.counterpartyId || !pair?.counterpartyKind || !Array.isArray(pair.passes)) return false;
      if (isReportActive) {
        const isSatUsed = usedEntities.satelliteIds.has(pair.satelliteId);
        let isCounterpartyUsed = false;
        if (pair.counterpartyKind === 'satellite') {
          isCounterpartyUsed = usedEntities.satelliteIds.has(pair.counterpartyId);
        } else if (pair.counterpartyKind === 'ground_station') {
          isCounterpartyUsed = usedEntities.groundStationIds.has(pair.counterpartyId);
        } else {
          isCounterpartyUsed = usedEntities.geoFeatureIds.has(pair.counterpartyId);
        }
        return isSatUsed && isCounterpartyUsed;
      }
      return true;
    });

    const modelStart = toEpoch(modelData?.propagation?.startEpoch);
    const modelEnd = toEpoch(modelData?.propagation?.endEpoch);
    const pairStarts = validPairs.map(pair => toEpoch(pair.fromUtc)).filter((v): v is number => v !== null);
    const pairEnds = validPairs.map(pair => toEpoch(pair.toUtc)).filter((v): v is number => v !== null);
    const passStarts = validPairs.flatMap(pair => pair.passes.map(pass => pass.start)).filter(Number.isFinite);
    const passEnds = validPairs.flatMap(pair => pair.passes.map(pass => pass.end)).filter(Number.isFinite);
    let windowStart = modelStart ?? (pairStarts.length ? Math.min(...pairStarts) : passStarts.length ? Math.min(...passStarts) : null);
    let windowEnd = modelEnd ?? (pairEnds.length ? Math.max(...pairEnds) : passEnds.length ? Math.max(...passEnds) : null);

    if (isReportActive && activeReport?.raw?.requestInput) {
      const input = activeReport.raw.requestInput;
      const reportStart = toEpoch(input.startTime);
      const duration = input.duration ?? input.durationHrs ?? input.durationHours ?? 24;
      if (reportStart !== null) {
        windowStart = reportStart;
        windowEnd = reportStart + (duration * 3600);
      }
    }

    const rowMap = new Map<string, TimelineRow>();
    const getRow = (pair: AccessWindowPair) => {
      const rowKey = `${pair.counterpartyKind}:${pair.counterpartyId}`;
      const existing = rowMap.get(rowKey);
      if (existing) return existing;
      const name = pair.counterpartyKind === 'satellite'
        ? satelliteNames.get(pair.counterpartyId)
        : pair.counterpartyKind === 'ground_station'
          ? gsNames.get(pair.counterpartyId)
          : geoNames.get(pair.counterpartyId);
      const row: TimelineRow = {
        id: rowKey,
        kind: pair.counterpartyKind,
        label: name ?? `${getKindLabel(pair.counterpartyKind)} ${pair.counterpartyId.slice(0, 8)}`,
        icon: getKindIcon(pair.counterpartyKind),
        blocks: [],
      };
      rowMap.set(rowKey, row);
      return row;
    };

    validPairs.forEach(pair => {
      const row = getRow(pair);
      pair.passes.forEach(pass => {
        if (!Number.isFinite(pass.start) || !Number.isFinite(pass.end) || pass.end <= pass.start) return;
        row.blocks.push({
          id: pass.passId || `${pair.pairKey}:${pass.start}`,
          pairKey: pair.pairKey,
          satelliteId: pair.satelliteId,
          satelliteName: satelliteNames.get(pair.satelliteId) ?? `Sat ${pair.satelliteId.slice(0, 8)}`,
          counterpartyId: pair.counterpartyId,
          counterpartyKind: pair.counterpartyKind,
          counterpartyName: row.label,
          status: pair.status,
          errorReason: pair.errorReason,
          start: pass.start,
          end: pass.end,
          durationSec: pass.durationSec ?? pass.end - pass.start,
          maxElevationDeg: pass.maxElevationDeg,
        });
      });
    });

    const rows = Array.from(rowMap.values())
      .map(row => ({ ...row, blocks: row.blocks.sort((a, b) => a.start - b.start) }))
      .sort((a, b) => {
        const order: Record<AccessWindowCounterpartyKind, number> = {
          geo_feature_spot: 0,
          geo_feature_geofence: 1,
          ground_station: 2,
          satellite: 3,
        };
        return order[a.kind] - order[b.kind] || a.label.localeCompare(b.label);
      });

    const targets: TimelineDataType = {
      name: 'Target Track',
      values: rows.filter(row => row.kind === 'geo_feature_spot') || [],
      id: 'geo_feature_spot',
      icon: 'Target'
    }
    const geofeatures: TimelineDataType = {
      name: 'Geofeature',
      values: rows.filter(row => row.kind === 'geo_feature_geofence') || [],
      id: 'geo_feature_geofence',
      icon: 'Polygon'
    }
    const stations: TimelineDataType = {
      name: 'Ground Stations',
      values: rows.filter(row => row.kind === 'ground_station') || [],
      id: 'ground_station',
      icon: 'Antenna'
    }
    const satellites: TimelineDataType = {
      name: 'Satellites',
      values: rows.filter(row => row.kind === 'satellite') || [],
      id: 'satellite',
      icon: 'Satellite'
    }
    const timelineData: TimelineDataType[] = [targets, geofeatures, stations, satellites].filter(section => section.values.length > 0);

    return { timelineData, rows, windowStart, windowEnd };
  }, [accessWindowPairs, geoFeatures, groundStations, modelData?.propagation, satelliteList, isReportActive, usedEntities]);


  const propagationStartISO = useMemo(() => {
    const epoch = modelData?.propagation?.startEpoch || windowStart;
    if (!epoch) return 'Not configured';
    const d = new Date(epoch * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}Z`;
  }, [modelData, windowStart]);

  const propagationDurationHrs = useMemo(() => {
    const start = modelData?.propagation?.startEpoch || windowStart;
    const end = modelData?.propagation?.endEpoch || windowEnd;
    if (!start || !end) return 0;
    return Math.round((end - start) / 3600);
  }, [modelData, windowStart, windowEnd]);

  const timelineStats = useMemo(() => {
    const blocks = rows.flatMap(row => row.blocks);
    const totalDuration = blocks.reduce((sum, block) => sum + Math.max(0, block.durationSec), 0);
    return {
      totalPasses: blocks.length,
      totalDuration,
    };
  }, [rows]);

  const isLiveState = updateState === 'success' || accessWindowStatus === 'ready';
  const isComputing = updateState === 'updating' || accessWindowStatus === 'computing';
  const updateLabel = isComputing ? 'Computing' : updateState === 'failed' ? 'Compute Access' : isLiveState ? 'Live' : 'Compute Access';
  const updateDisabled = isLiveState || !hasSatellites || updateLocked || !updateReady || updateState === 'updating' || accessWindowStatus === 'computing';
  const updateTitle = hasSatellites
    ? updateReady ? 'Compute access windows for satellites, GF and ground stations' : updateDisabledReason
    : 'Add satellites to enable mission update';
  const hasTimelineContent = timelineStats.totalPasses > 0;
  const resizeDisabled = !hasTimelineContent || timelineCollapsed;

  const showAccessLiveButton = satelliteList?.length > 1 || (satelliteList?.length > 0 && (groundStations?.length > 0 || geoFeatures?.length > 0))

  useEffect(() => {
    if (!hasTimelineContent) {
      hadTimelineContentRef.current = false;
      if (!timelineCollapsed) setTimelineCollapsed(true);
      return;
    }
    if (!hadTimelineContentRef.current) {
      hadTimelineContentRef.current = true;
      if (timelineCollapsed) setTimelineCollapsed(false);
    }
  }, [hasTimelineContent, setTimelineCollapsed, timelineCollapsed]);

  useEffect(() => {
    if (!playbackDisabled || !playing) return;
    setPlaying(false);
    cesium.setClockPlaying(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playbackDisabled, playing]);

  useEffect(() => {
    if (timeEditing) timeInputRef.current?.select();
  }, [timeEditing]);

  useEffect(() => {
    if (!updateReady && updateState === 'success') setUpdateState('idle');
  }, [updateReady, updateState]);

  useEffect(() => {
    if (accessWindowStatus !== 'ready' && updateState === 'success') {
      setUpdateState('idle');
    }
  }, [accessWindowStatus, updateState]);

  const togglePlay = () => {
    if (playbackDisabled) return;
    const next = !playing;
    setPlaying(next);
    cesium.setClockPlaying(next);
  };

  const changeSpeed = (direction: 1 | -1) => {
    if (playbackDisabled) return;
    const currentIndex = Math.max(0, SPEEDS.indexOf(speed));
    const nextIndex = Math.max(0, Math.min(SPEEDS.length - 1, currentIndex + direction));
    const next = SPEEDS[nextIndex];
    setSpeed(next);
    cesium.setClockMultiplier(next);
  };

  const changeViewerTime = (value: number) => {
    const v = cesium.viewerRef.current;
    if (!v) return;

    const modelStart = toEpoch(modelData?.propagation?.startEpoch);
    const modelEnd = toEpoch(modelData?.propagation?.endEpoch);

    let constrainedValue = value;
    if (modelStart !== null && constrainedValue < modelStart) {
      constrainedValue = modelStart;
    }
    if (modelEnd !== null && constrainedValue > modelEnd) {
      constrainedValue = modelEnd;
    }

    const julianDate = Cesium.JulianDate.fromDate(new Date(constrainedValue * 1000));
    v.clock.currentTime = julianDate;
  };

  const handleSkipToStart = () => {
    if (playbackDisabled) return;
    cesium.resetClockToModelStart();
    setPlaying(false);
    cesium.setClockPlaying(false);
  };

  const handleSkipToEnd = () => {
    if (playbackDisabled) return;
    const endEpoch = modelData?.propagation?.endEpoch;
    if (!endEpoch) return;
    cesium.setClockTime(new Date(endEpoch * 1000));
    setPlaying(false);
    cesium.setClockPlaying(false);
  };

  const handleUpdate = async () => {
    if (!onMissionUpdate || updateDisabled) return;
    setUpdateState('updating');
    try {
      await onMissionUpdate();
      setUpdateState('success');
    } catch {
      setUpdateState('failed');
    }
  };

  const enterTimeEdit = () => {
    setTimeDraft(time);
    setTimeEditing(true);
  };

  const commitTimeEdit = () => {
    setTimeEditing(false);
    if (!timeDraft.trim()) return;
    const parsed = new Date(timeDraft.trim().replace(' ', 'T') + 'Z');
    if (!isNaN(parsed.getTime())) {
      const timeSec = Math.floor(parsed.getTime() / 1000);
      const modelStart = toEpoch(modelData?.propagation?.startEpoch);
      const modelEnd = toEpoch(modelData?.propagation?.endEpoch);

      if (modelStart !== null && timeSec < modelStart) {
        showToastMessage('error', `Time cannot be earlier than Absolute Start Time (${epochToDisplay(modelStart)} UTC)`);
        return;
      }
      if (modelEnd !== null && timeSec > modelEnd) {
        showToastMessage('error', `Time cannot be later than Absolute End Time (${epochToDisplay(modelEnd)} UTC)`);
        return;
      }

      cesium.setClockTime(parsed);
    }
  };

  const handleTimeKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') commitTimeEdit();
    if (event.key === 'Escape') setTimeEditing(false);
  };

  const toggleSection = (kind: AccessWindowCounterpartyKind) => {
    setCollapsedSections(prev => ({ ...prev, [kind]: !prev[kind] }));
  };


  const handleResizePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (resizeDisabled) return;
    resizeRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startHeight: timelineHeight,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const handleResizePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const resize = resizeRef.current;
    if (!resize || resize.pointerId !== event.pointerId) return;
    const nextHeight = resize.startHeight - (event.clientY - resize.startY);
    setTimelineHeight(Math.max(TIMELINE_MIN_HEIGHT, Math.min(TIMELINE_MAX_HEIGHT, nextHeight)));
  };

  const handleResizePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (resizeRef.current?.pointerId !== event.pointerId) return;
    resizeRef.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // pointer capture may already be released by the browser
    }
  };

  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const [openSelect, setOpenSelect] = useState<null | string>(null)
  const [selectedFrame, setSelectedFrame] = useState(TimeFrameOptions[0])
  const onSelectFrame = (frame: string) => {
    const value = TimeFrameInMinutes[frame]
    setSelectedFrame(frame)
    if ([30, 60].includes(value) && scrollAreaRef.current) {
      scrollAreaRef.current.scrollLeft = 0
    }
  }

  const simulationStart = windowStart || 0
  const simulationEnd = windowEnd || 0
  const simulationDuration = (simulationEnd - simulationStart) / 60

  const timelineStart = simulationStart;
  const timelineDuration = isReportActive ? simulationDuration : Math.max(simulationDuration, MINIMUM_TIMELINE_DURATION);
  const timelineEnd = timelineStart + (timelineDuration * 60)

  const showTimeline = useMemo(() => Boolean(timelineStart && timelineEnd && timelineDuration && currentEpoch), [timelineStart, timelineEnd, timelineDuration, currentEpoch])

  return (
    <div
      className={styles.timelineOuterWrapper}
      style={{
        position: 'absolute',
        bottom: '28px',
        width: '100%',
        zIndex: 8,
        height: 'auto',
        maxHeight: timelineHeight,
        background: 'black',
        padding: '0px 10px',
      }}
    >

      <CommonTimeline
        scrollAreaRef={scrollAreaRef}
        timelineDuration={timelineDuration}
        timelineStartTime={timelineStart}
        timelineEndTime={timelineEnd}
        selectedFrame={TimeFrameInMinutes[selectedFrame]}
        hideToggleButton={false}
        disabledToggleButton={!hasTimelineContent}
        show={showTimeline}
        sidebarSize={142}
        active={!timelineCollapsed}
        onToggle={(active) => setTimelineCollapsed(!active)}
      >
        <div
          className={`${styles.resizeHandle}`}
          onPointerDown={handleResizePointerDown}
          onPointerMove={handleResizePointerMove}
          onPointerUp={handleResizePointerUp}
          onPointerCancel={handleResizePointerUp}
          role="separator"
          aria-orientation="horizontal"
          aria-hidden={resizeDisabled}
        />
        <TimelineHeader>
          <div className={styles.header}>
            <div className={styles.stats}>
              <div className={styles.modelConfigContainer}>
                {timeEditing ? (
                  <CustomToolTip title="Press Enter to apply" placement="top">
                    <input
                      ref={timeInputRef}
                      className={styles.timeInput}
                      value={timeDraft}
                      onChange={event => setTimeDraft(event.target.value)}
                      onBlur={commitTimeEdit}
                      onKeyDown={handleTimeKeyDown}
                      placeholder="YYYY-MM-DD HH:MM:SS"
                    />
                  </CustomToolTip>
                ) : (
                  <>
                    <span className={styles.timeVal}>{time}</span>
                    <CustomToolTip title="Edit observer time">
                      <button
                        className={styles.editButton}
                        type="button"
                        onClick={enterTimeEdit}
                      >
                        <EditIcon />
                      </button>
                    </CustomToolTip>
                  </>
                )}
              </div>
              <span className={styles.utc}>UTC</span>
            </div>

            <div className={styles.playback}>
              <CustomToolTip title={playbackDisabled ? '' : 'Jump to start'}>
                <button className={styles.ctrl} type="button" onClick={handleSkipToStart} disabled={playbackDisabled}><SkipBackIcon /></button>
              </CustomToolTip>
              <CustomToolTip title={playbackDisabled ? '' : `Decrease speed (${speed}×)`}>
                <button className={styles.ctrl} type="button" onClick={() => changeSpeed(-1)} disabled={playbackDisabled || speed === SPEEDS[0]}><StepBackIcon /></button>
              </CustomToolTip>
              <CustomToolTip title={playbackDisabled ? '' : playing ? 'Pause' : 'Play'}>
                <button className={`${styles.ctrl} ${styles.playBtn}`} type="button" onClick={togglePlay} disabled={playbackDisabled}>
                  {playing ? <PauseIcon /> : <PlayIcon />}
                </button>
              </CustomToolTip>
              <CustomToolTip title={playbackDisabled ? '' : `Increase speed (${speed}×)`}>
                <button className={styles.ctrl} type="button" onClick={() => changeSpeed(1)} disabled={playbackDisabled || speed === SPEEDS[SPEEDS.length - 1]}><StepFwdIcon /></button>
              </CustomToolTip>
              <CustomToolTip title={playbackDisabled ? '' : 'Skip to model end'}>
                <button className={styles.ctrl} type="button" onClick={handleSkipToEnd} disabled={playbackDisabled}><SkipFwdIcon /></button>
              </CustomToolTip>
            </div>

            <div className={styles.actions}>
              <CustomToolTip title="Simulation speed">
                <div style={{ display: 'inline-flex' }}>
                  <SpeedoMeter value={speed} />
                </div>
              </CustomToolTip>
              {showAccessLiveButton && !isLiveState &&
                <CustomToolTip title={updateTitle} placement="top">
                  <button
                    className={[
                      styles.updateBtn,
                      updateState === 'updating' || isComputing ? styles.updateBtnLoading : '',
                      isLiveState ? styles.updateBtnLive : '',
                      updateState === 'failed' && !isComputing ? styles.updateBtnFailed : '',
                    ].filter(Boolean).join(' ')}
                    type="button"
                    onClick={handleUpdate}
                    disabled={updateDisabled}
                  >
                    {(updateState === 'updating' || isComputing) && <span className={styles.updateSpinner} />}
                    {!isLiveState && <RefreshIcon />}
                    <span>{updateLabel}</span>
                  </button>
                </CustomToolTip>
              }
              {showAccessLiveButton && isLiveState && <LiveButton onClick={() => { }} />}
              <CustomToolTip title="Select timeframe">
                <div className='updated__omni__select__border'>
                  <OmniSelect
                    id='omni__select__timeframe'
                    open={openSelect}
                    setOpen={setOpenSelect}
                    disabled={false}
                    className=""
                    options={TimeFrameOptions}
                    value={selectedFrame}
                    onChange={onSelectFrame}
                    headerOption={{
                      title: 'Timeframe',
                      icon: <TimeIntervalSvg />
                    }}
                  />
                </div>
              </CustomToolTip>

            </div>
          </div>
        </TimelineHeader>
        <TimelineBodyContainer>
          <TimelineScrollContainer className={styles.timeline__scroll__container}>
            <TimelineContainer className={`${styles.timeline__time__track}`}>
              <TimelineTrackContainer>
                <TimelineTrack />
                {isLiveState &&
                  <RealtimeTracker
                    strtTime={simulationStart}
                    endTime={simulationEnd}
                    duration={simulationDuration}
                    onChange={changeViewerTime}
                    currentTime={currentEpoch || timelineStart}
                  />
                }
              </TimelineTrackContainer>
            </TimelineContainer>

            {timelineData.map((data, index) => {
              const collapsed = collapsedSections[data.id] || false
              return (
                <Fragment key={data.id}>
                  <TimelineContainer
                    sidebarDetails={{
                      asChild: true,
                      name: data.name,
                      children: (
                        <div
                          onClick={() => toggleSection(data.id)}
                          className={`${styles.toggle__button__container} ${collapsed ? styles.toggle__button__collapsed : ''}`}>
                          <RightArrow />
                          <span className={styles.toggle__button__text}>{data.name}</span>
                        </div>
                      )
                    }}
                  >
                    <TimelineEventTrack></TimelineEventTrack>
                  </TimelineContainer>
                  {!collapsed && data.values.map(value => {
                    return (
                      <TimelineContainer
                        key={value.id}
                        sidebarDetails={{
                          className: styles.timeline__sidebar__text__container,
                          name: value.label,
                        }}
                      >
                        <TimelineEventTrack>
                          {value.blocks.map((event, blockIdx) => {
                            const startTime = event.start;
                            const endTime = event.end;
                            const isStale = event.status === 'stale';
                            const isFailed = event.status === 'failed';
                            const isActive = currentEpoch !== null && currentEpoch >= startTime && currentEpoch <= endTime;
                            return (
                              <TimelineEvent key={`${event.id}-${event.satelliteId}-${blockIdx}`} eventStart={startTime} eventEnd={endTime}>
                                <div
                                  className={[
                                    styles.timeline__event__container,
                                    isStale ? styles.passStale : '',
                                    isFailed ? styles.passFailed : '',
                                    isActive ? styles.passActive : '',
                                  ].filter(Boolean).join(' ')}
                                  onMouseEnter={(e) => {
                                    setHoveredEvent(event);
                                    setTooltipPos({ x: e.clientX, y: e.clientY });
                                  }}
                                  onMouseMove={(e) => {
                                    setTooltipPos({ x: e.clientX, y: e.clientY });
                                  }}
                                  onMouseLeave={() => {
                                    setHoveredEvent(null);
                                    setTooltipPos(null);
                                  }}
                                >
                                  <span className={styles.timeline__event__text}>{event.counterpartyName}</span>
                                </div>
                              </TimelineEvent>
                            )
                          })}
                        </TimelineEventTrack>
                      </TimelineContainer>
                    )
                  })}
                  <TimelineContainerDevider />
                </Fragment>
              )
            })}
          </TimelineScrollContainer>
        </TimelineBodyContainer>
      </CommonTimeline>

      {hoveredEvent && tooltipPos && (
        <div
          className={styles.tooltip}
          style={{
            left: `${Math.min(tooltipPos.x + 12, window.innerWidth - 220)}px`,
            top: `${Math.min(tooltipPos.y + 12, window.innerHeight - 100)}px`,
          }}
        >
          <div className={styles.tooltipTitle}>
            {hoveredEvent.satelliteName} - {hoveredEvent.counterpartyName}
          </div>
          <div>
            <span className={styles.tooltipLabel}>Start - </span>
            <span className={styles.tooltipValue}>
              {formatTimelineDate(hoveredEvent.start).date} {formatTimelineDate(hoveredEvent.start).time}
            </span>
          </div>
          <div>
            <span className={styles.tooltipLabel}>End - </span>
            <span className={styles.tooltipValue}>
              {formatTimelineDate(hoveredEvent.end).date} {formatTimelineDate(hoveredEvent.end).time}
            </span>
          </div>
          <div>
            <span className={styles.tooltipLabel}>Duration - </span>
            <span className={styles.tooltipValue}>
              {formatTimelineDuration(hoveredEvent.durationSec)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Timeline;
